/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectEmbedToken,
  selectHotelName,
  selectTabList,
} from 'modules/dashboard/selectors';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import {
  selectIsCumulative,
  selectIsSegmentDetailed,
  selectTrendSegmentChartBreakdown,
  selectTrendSegmentFocusOn,
  selectTrendTimeWeeklyView,
  selectRevenueIsDisplayTS,
  selectTrendSegmentTabularVisuals,
  selectTrendSegmentCumulativeTabularVisuals,
  selectTrendSegmentWeeklyTabularVisuals,
  selectTrendSegmentPercentageTabularVisuals,
  selectIsPercentageChange,
  selectTrendHotelNormalizedView,
  selectTrendSegmentTabularFilterList,
  selectTrendHotelFocusOn,
  selectActiveTrendTab,
  selectIsMovingAverage,
  selectMovingAverageTabularVisualTS,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import ExportToCSV, {
  getPbiExportData,
} from 'modules/dashboard/components/tab-container/export-visual-data/export-csv';
import { EXPORT_OPTIONS, REPORT_TYPE } from 'modules/dashboard/constants';
import { AssistantIcon } from 'modules/common/components';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { isEmpty } from '../../../../../../../../common/helpers/object';
/**
 * Trend Segment tabular report with filters
 */
const TrendSegmentTabularWidget = ({ setIsSegmentLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectTrendSegmentTabularFilterList);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const visualFilters = useSelector(selectVisualFilters);
  const visuals = useSelector(selectTrendSegmentTabularVisuals);
  const weeklyVisuals = useSelector(selectTrendSegmentWeeklyTabularVisuals);
  const cumulativeVisuals = useSelector(selectTrendSegmentCumulativeTabularVisuals);
  const tokenDetails = useSelector(selectEmbedToken);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayTS);
  const percentageVisuals = useSelector(selectTrendSegmentPercentageTabularVisuals);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const normalizedViewToggle = useSelector(selectTrendHotelNormalizedView);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const activeTab = useSelector(selectActiveTrendTab);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverageSegmentTabularVisuals = useSelector(selectMovingAverageTabularVisualTS);
  const currentUser = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [tabularCombined, setTabularCombined] = useState({});
  const [isTabularCombinedRendered, setIsTabularCombinedRendered] = useState(false);
  const [isV1Processing, setIsV1Processing] = useState(false);

  // To get visual details
  const getVisual = (index) => {
    let found;
    if (isCumulative) {
      found = cumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (weeklyViewToggle) {
      found = weeklyVisuals?.find((visual) => visual?.order === index);
    } else if (isPercentageChange && !weeklyViewToggle && !isCumulative && !normalizedViewToggle) {
      found = percentageVisuals?.find((visual) => visual?.order === index);
    } else if (isMovingAverage && !weeklyViewToggle && !isPercentageChange) {
      found = movingAverageSegmentTabularVisuals?.find((visual) => visual?.order === index);
    } else {
      found = visuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = tabularFilterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(r) => setIsLoaded(r)}
          setIsRendered={(d) => setIsRendered(d)}
          tab={TAB_VALUE.TREND_TIME}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  const getExcelExportData = async () => {
    const data = await getPbiExportData({
      isLoaded: isTabularCombinedRendered,
      fileName: isPercentageChange
        ? 'TVS_Percentage'
        : isMovingAverage
        ? 'CMATS_Daily'
        : isCumulative
        ? 'TVS_Cumulative'
        : weeklyViewToggle
        ? 'TVS_Weekly'
        : 'TVS_Daily',
      currentUser,
      selectedHotel,
      report: tabularCombined,
      isInterpreterAgent: true,
    });
    return data;
  };
  //
  const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
  const breakdown = `Breakdown : , ${selectedBreakdown}\r\n`;
  const focus = focusOn ? focusOn?.map((item) => item?.label).toString() : '';
  const focusedBreakdown = `Focused Breakdown Segments: , "${focus.toString()}"\r\n`;
  //
  return (
    !isEmpty(tabularFilterList) && (
      <VisualGrid
        isDisplay={revenueIsDisplay}
        visualName={getVisualName(0)}
        visual={getWidget(
          0,
          styles.tabularVisual,
          setTabularCombined,
          setIsSegmentLoaded,
          setIsTabularCombinedRendered,
          trendActions.setRevenueIsDisplayTS
        )}
        onClick={() => {
          dispatch(trendActions.setRevenueIsDisplayTS(!revenueIsDisplay));
        }}
        toggleFragment={
          <ExportToCSV
            exportExcel
            report={tabularCombined}
            isLoaded={isTabularCombinedRendered}
            fileName={
              isPercentageChange
                ? 'TVS_Percentage'
                : isMovingAverage
                ? 'CMATS_Daily'
                : isCumulative
                ? 'TVS_Cumulative'
                : weeklyViewToggle
                ? 'TVS_Weekly'
                : 'TVS_Daily'
            }
            weeklyViewToggle={weeklyViewToggle}
            isCumulative={isCumulative}
            otherFilters={breakdown.concat(focusedBreakdown)}
          />
        }
        isWorkbookHotelComparison={activeTab === REPORT_TYPE.TREND_HOTEL}
        comparisonHotel={hotelFocusOn}
        displayHotelName
        interpreterFragment={
          <AssistantIcon
            visualName={getVisualName(0)}
            report={tabularCombined}
            isProcessing={isV1Processing}
            setIsProcessing={setIsV1Processing}
            disabled={!isTabularCombinedRendered || isV1Processing || !revenueIsDisplay}
            getExcelExportData={getExcelExportData}
            exportType={exportType}
            setExportType={setExportType}
          />
        }
      />
    )
  );
};
//
export default TrendSegmentTabularWidget;
